import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import {
  getFirstAvailableLocation,
  getMembershipInfoFromLocation,
  MembershipDetail,
  PartnerOffering,
  PartnerOfferingLocation,
  shouldHideLocationAndCityPicker,
} from "../../data/partner_offering";
import {
  Space,
  Location,
  QuoteItem,
  QuoteItemPrice,
  useMembershipStandardizeImages,
  ImageVersion,
  DayUnitQuoteItemPrice,
} from "../../data";
import { FirstDayOfWeek, toDay } from "../../lib/datetime";
import { useI18n } from "../../lib/i18n/i18n_provider";
import {
  AvailableDay,
  AvailableTimeSlot,
  BookingType,
  Order,
  OrderableType,
  OrderItem,
  OrderStatus,
  Quote,
  SpaceOffering,
  useGetAvailableDaysMembership,
  useQuoteV2Query,
  useSpaceBookingPolicyQuery,
  useSpaceMaybeQuery,
  useValidatePromoQuery,
} from "../../offering/api";
import {
  DailyAccessOrderParams,
  DailyAccessOrderParamsErrors,
  DailyOfficeOrderParams,
  DailyOfficeOrderParamsErrors,
  DayPassDateRange,
  EventSpaceOrderParams,
  MeetingRoomDateRange,
  MeetingRoomOrderParams,
  MeetingRoomOrderParamsErrors,
  MembershipOrderParams,
  MembershipOrderParamsErrors,
} from "../../offering/order_params";
import {
  Button,
  Card,
  Col,
  Container as BootstrapContainer,
  ListGroup,
  Modal,
  Row,
} from "react-bootstrap";
import {
  Icon,
  Hoverable,
  Popover,
  Container,
  cssVariables,
  Divider,
} from "../../components";
import {
  formatDayRange,
  formatMeetingRoomDateRange,
} from "../../offering/format_date_range";
import { useFormik } from "formik";
import {
  DailyAccessCheckoutBoxDesktopV2,
  DailyOfficeCheckoutBoxDesktop,
  MeetingRoomCheckoutBoxDesktopV2,
  MembershipCheckoutBoxDesktopV2,
  MembershipCheckoutPriceV2,
} from "./checkout_box";

import "./quote_view.css";
import { useMediaQuery, useQueryString, useToggle } from "../../hooks";
import { orderItemToOrderParams } from "../../offering/template_offering_confirm_order_base";
import { Spacer } from "../../offering/spacer";
import { format, parseISO } from "date-fns";
import { getFirstDayOfNextMonthAfterNMonths } from "../../offering/date_utils";
import { MembershipPriceInfo } from "./membership_price_info";
import { PulseLoader } from "../../components/pulse_loader";
import { monthDifference, parseDay } from "../../offering/day_utils";
import pluralize from "pluralize";
import { getFieldFromObject } from "../utils/get_field_from_object";
import { CreditOption } from "./credit_quantity_picker";
import { getCheckoutCancellationPolicyText } from "../../offering/get_checkout_cancellation_policy_text";
import { getPromoMonthsLeftAfterFirstPayment } from "../utils/get_promo_months_left_after_first_payment";
import {
  DateTimeValue,
  isHourlyValue,
} from "./date-time-picker/date_time_picker";
import { AdditionalFeesItem } from "./additional_fees_item";
import { getAllQuoteFeeLines } from "@/fm/utils/get_all_fee_lines";
import { EventBookingContainer } from "./event_and_layout/event_booking_container";
import { useGetEventTypes } from "../hooks/use_get_event_types";
import { colors } from "./colors";
import { parseTime } from "@/offering/time_utils";
import { usePriceBreakdownPerHourFeatureFlag } from "@/core/splitio";
import {
  getEndDateFromOrderParams,
  getStartDateFromOrderParams,
} from "@/offering/order_params_utils";

interface QuoteViewDailyAccessDesktopProps {
  quote: Quote | undefined;
  quoteLoading: boolean;
  quoteError: string;
  includeQuantity: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DailyAccessOrderParams;
  onResetOrderParams: (orderParams: DailyAccessOrderParams) => void;
  orderParamsErrors?: DailyAccessOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onChangeQuantity: (qty: number) => void;
  multiPasses: SpaceOffering[];
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onContinue?: () => void;
  onUpdateQuote: (isUpdate: boolean) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  offeringName: string;
  partnerID: string;
  promotableType: string;
  promotableID?: string;
  email?: string;
  onChangePromoCode: (value: string) => void;
  datePickerPlaceholder?: string;
  showDiscountLabel?: boolean;
  availableDays?: Date[];
  multiDayBookingAllowed: boolean;
  credits: CreditOption[];
  singleDaySelect?: boolean;
}

export function QuoteViewDailyAccessDesktop(
  props: QuoteViewDailyAccessDesktopProps
) {
  const {
    includeQuantity,
    quote,
    quoteError,
    quoteLoading,
    multiPasses,
    location,
    orderParams,
    orderParamsErrors,
    onResetOrderParams,
    locations,
    space,
    onChangeLocation,
    onChangeCity,
    onChangeAddOnID,
    onChangeDateRange,
    onChangeQuantity,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    onUpdateQuote,
    firstDayOfWeek,
    offeringName,
    partnerID,
    promotableType,
    promotableID,
    onChangePromoCode,
    datePickerPlaceholder,
    showDiscountLabel,
    availableDays,
    multiDayBookingAllowed,
    credits,
    singleDaySelect,
  } = props;

  const [openUpdate, setOpenUpdate] = useState<boolean>(false);
  const [initialOrderParams, setInitialOrderParams] =
    useState<DailyAccessOrderParams>(orderParams);
  const [initQuote, setInitQuote] = useState(quote);
  const mq = useMediaQuery();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const handleUpdateBooking = useCallback(() => {
    onUpdateQuote(true);
    setOpenUpdate(false);
    setInitialOrderParams(orderParams);
  }, [onUpdateQuote, orderParams]);

  const handleOpenDetailsBooking = useCallback(() => {
    setOpenUpdate(true);
    onUpdateQuote(false);
  }, [onUpdateQuote, setOpenUpdate]);

  const handleClosedUpdateBookingDetails = useCallback(() => {
    setOpenUpdate(false);
    onUpdateQuote(false);
    onResetOrderParams(initialOrderParams);
  }, [onUpdateQuote, setOpenUpdate, onResetOrderParams, initialOrderParams]);

  const handleChangePromoCode = useCallback(
    (code: string) => {
      setInitialOrderParams(orderParams);
      onChangePromoCode(code);
    },
    [onChangePromoCode, orderParams]
  );

  useEffect(() => {
    if (quote) {
      setInitQuote(quote);
    } else if (!!quoteError) {
      setOpenUpdate(true);
    }
  }, [quote, quoteError]);

  if (!initQuote) {
    return <PulseLoader />;
  }

  return (
    <>
      <Card className={mq.device === "mobile" ? "quote-view-mobile-card" : ""}>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <SpaceView
              offeringName={offeringName}
              quote={initQuote}
              onUpdateDetailsBooking={handleOpenDetailsBooking}
              testId="daily-acces-space-info"
            />
          </ListGroup.Item>

          {initQuote && (
            <div
              className="checkout-daily-access-payment-info"
              data-testid="checkout-daily-access-payment-info"
            >
              <ListGroup.Item>
                <QuoteDetails
                  quote={initQuote}
                  currency={space?.currencyCode || null}
                />
              </ListGroup.Item>

              <ListGroup.Item>
                <PromoCodeBox
                  partnerID={partnerID}
                  promotableType={promotableType}
                  promotableID={promotableID}
                  email={orderParams.email}
                  quote={initQuote}
                  currency={space?.currencyCode}
                  onChangePromoCode={(code) => handleChangePromoCode(code)}
                  dayPassDateRange={
                    orderParams.type === "DailyAccess"
                      ? orderParams.dateRange
                      : undefined
                  }
                  quantity={
                    orderParams.type === "DailyAccess"
                      ? orderParams.quantity
                      : undefined
                  }
                />
              </ListGroup.Item>

              <ListGroup.Item>
                <ListGroup>
                  <ListGroup.Item className="border-0">
                    <TotalPrice
                      total={initQuote.totalPrice}
                      currency={space?.currencyCode || null}
                    />
                  </ListGroup.Item>
                </ListGroup>
              </ListGroup.Item>
              {!orderParams.email && bookingPolicy?.checkoutWarning && (
                <CheckoutWarning message={bookingPolicy.checkoutWarning} />
              )}
            </div>
          )}
        </ListGroup>
      </Card>
      <UpdateBookingDetails
        onClose={handleClosedUpdateBookingDetails}
        open={openUpdate}
        offeringName={offeringName}
        onUpdateBooking={handleUpdateBooking}
        space={space}
        checkoutDetails={
          <DailyAccessCheckoutBoxDesktopV2
            includeQuantity={includeQuantity}
            datePickerPlaceholder={datePickerPlaceholder}
            orderParamsErrors={orderParamsErrors}
            firstDayOfWeek={firstDayOfWeek}
            orderParams={orderParams}
            multiPasses={multiPasses || []}
            isOutsideRange={isOutsideRange}
            isBlockedDate={isBlockedDate}
            onChangeAddOnID={onChangeAddOnID}
            onChangeLocation={onChangeLocation}
            onChangeCity={onChangeCity}
            onChangeDateRange={onChangeDateRange}
            onChangeQuantity={onChangeQuantity}
            onContinue={onContinue}
            onNextVisibleDates={onNextVisibleDates}
            locations={locations}
            location={location}
            quote={quote ? quote : undefined}
            space={space}
            quoteLoading={quoteLoading}
            quoteError={quoteError}
            showCTA={false}
            showDiscountLabel={showDiscountLabel}
            availableDays={availableDays}
            multiDayBookingAllowed={multiDayBookingAllowed}
            credits={credits}
            singleDaySelect={singleDaySelect}
          />
        }
      />
    </>
  );
}

interface QuoteViewMeetingRoomDesktopProps {
  quote?: Quote;
  quoteError: string;
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: MeetingRoomOrderParams;
  onResetOrderParams: (orderParams: MeetingRoomOrderParams) => void;
  onUpdateQuote: (isUpdate: boolean) => void;
  orderParamsErrors?: MeetingRoomOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: DateTimeValue) => void;
  onContinue?: () => void;
  onSelectDate: (date: Date) => void;
  onChangeAttendees: (value: string) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  timeslots: AvailableTimeSlot[];
  firstDayOfWeek?: FirstDayOfWeek;
  availableDaysError?: string;
  availableTimeSlotsError?: string;
  offeringName: string;
  partnerID: string;
  onChangePromoCode: (value: string) => void;
  datePickerPlaceholder?: string;
  availableDays?: AvailableDay[];
  disableRangeSelection?: boolean;
}

export function QuoteViewMeetingRoomDesktop(
  props: QuoteViewMeetingRoomDesktopProps
) {
  const {
    quote,
    quoteError,
    location,
    orderParams,
    onResetOrderParams,
    onUpdateQuote,
    orderParamsErrors,
    space,
    timeslots,
    firstDayOfWeek,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    onChangeAttendees,
    availableDaysError,
    availableTimeSlotsError,
    offeringName,
    datePickerPlaceholder,
    partnerID,
    onChangePromoCode,
    availableDays,
    disableRangeSelection,
  } = props;
  const [openUpdate, setOpenUpdate] = useState<boolean>(false);
  const [initialOrderParams, setInitialOrderParams] =
    useState<MeetingRoomOrderParams>(orderParams);
  const [initQuote, setInitQuote] = useState(quote);
  const mq = useMediaQuery();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const handleUpdateBooking = useCallback(() => {
    onUpdateQuote(true);
    setOpenUpdate(false);
    setInitialOrderParams(orderParams);
  }, [onUpdateQuote, orderParams]);

  const handleOpenDetailsBooking = useCallback(() => {
    setOpenUpdate(true);
    onUpdateQuote(false);
  }, [onUpdateQuote, setOpenUpdate]);

  const handleClosedUpdateBookingDetails = useCallback(() => {
    setOpenUpdate(false);
    onUpdateQuote(false);
    onResetOrderParams(initialOrderParams);
  }, [onUpdateQuote, setOpenUpdate, onResetOrderParams, initialOrderParams]);

  const handleChangePromoCode = useCallback(
    (code: string) => {
      setInitialOrderParams(orderParams);
      onChangePromoCode(code);
    },
    [onChangePromoCode, orderParams]
  );

  useEffect(() => {
    if (quote) {
      setInitQuote(quote);
    } else if (!!quoteError) {
      setOpenUpdate(true);
    }
  }, [quote, quoteError]);

  if (!initQuote) {
    return <PulseLoader />;
  }

  return (
    <>
      {space && location && (
        <>
          <Card
            className={mq.device === "mobile" ? "quote-view-mobile-card" : ""}
          >
            <ListGroup variant="flush">
              <ListGroup.Item>
                <SpaceView
                  offeringName={offeringName}
                  quote={initQuote}
                  onUpdateDetailsBooking={handleOpenDetailsBooking}
                  testId={"check-out-meeting-room-space-info"}
                />
              </ListGroup.Item>
              {initQuote && (
                <div
                  className="checkout-meeting-room-payment-info"
                  data-testid={"checkout-meeting-room-payment-info"}
                >
                  <ListGroup.Item>
                    <QuoteDetails
                      quote={initQuote}
                      currency={space.currencyCode}
                    />
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <PromoCodeBox
                      partnerID={partnerID}
                      promotableType="Space"
                      promotableID={orderParams.spaceID}
                      email={orderParams.email}
                      timeRange={
                        isHourlyValue(orderParams.dateRange)
                          ? orderParams.dateRange
                          : undefined
                      }
                      dayPassDateRange={
                        isHourlyValue(orderParams.dateRange)
                          ? undefined
                          : orderParams.dateRange
                      }
                      quote={initQuote}
                      currency={space?.currencyCode}
                      onChangePromoCode={(code) => handleChangePromoCode(code)}
                    />
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <ListGroup>
                      <ListGroup.Item className="border-0">
                        <TotalPrice
                          total={initQuote.totalPrice}
                          currency={space.currencyCode}
                        />
                      </ListGroup.Item>
                    </ListGroup>
                  </ListGroup.Item>
                  {!orderParams.email && bookingPolicy?.checkoutWarning && (
                    <CheckoutWarning message={bookingPolicy.checkoutWarning} />
                  )}
                </div>
              )}
            </ListGroup>
          </Card>
          <UpdateBookingDetails
            onClose={handleClosedUpdateBookingDetails}
            open={openUpdate}
            onUpdateBooking={handleUpdateBooking}
            offeringName={offeringName}
            space={space}
            checkoutDetails={
              <MeetingRoomCheckoutBoxDesktopV2
                firstDayOfWeek={firstDayOfWeek}
                orderParams={orderParams}
                orderParamsErrors={orderParamsErrors}
                isOutsideRange={isOutsideRange}
                isBlockedDate={isBlockedDate}
                onSelectDate={onSelectDate}
                onChangeAttendees={onChangeAttendees}
                timeslots={timeslots}
                quote={quote}
                onChangeDateRange={onChangeDateRange}
                onNextVisibleDates={onNextVisibleDates}
                availableDaysError={availableDaysError}
                availableTimeSlotsError={availableTimeSlotsError}
                datePickerPlaceholder={datePickerPlaceholder}
                space={space}
                onContinue={onContinue}
                showCTA={false}
                availableDays={availableDays}
                disableRangeSelection={disableRangeSelection}
              />
            }
          />
        </>
      )}
    </>
  );
}

interface QuoteViewEventDesktopProps {
  quote?: Quote;
  quoteError: string;
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: EventSpaceOrderParams;
  onResetOrderParams: (orderParams: EventSpaceOrderParams) => void;
  onUpdateQuote: (isUpdate: boolean) => void;
  offeringName: string;
  partnerID: string;
  onChangePromoCode: (value: string) => void;
  availableDays?: AvailableDay[];
  timeslots: AvailableTimeSlot[];
  disableRangeSelection?: boolean;
  onSelectDate: (date: Date) => void;
  onChangeDateRange: (dateRange?: DateTimeValue) => void;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeEventType: (id?: string, description?: string) => void;
  onChangeLayout: (id?: string) => void;
  onChangeNumberOfGuests: (value?: number) => void;
}

export function QuoteViewEventSpaceDesktop(props: QuoteViewEventDesktopProps) {
  const {
    quote,
    quoteError,
    location,
    orderParams,
    onResetOrderParams,
    onUpdateQuote,
    space,
    offeringName,
    partnerID,
    onChangePromoCode,
    availableDays,
    timeslots,
    disableRangeSelection,
    onSelectDate,
    onChangeDateRange,
    onNextVisibleDates,
    onChangeEventType,
    onChangeLayout,
    onChangeNumberOfGuests,
  } = props;
  const [openUpdate, setOpenUpdate] = useState<boolean>(false);
  const [initialOrderParams, setInitialOrderParams] =
    useState<EventSpaceOrderParams>(orderParams);
  const [initQuote, setInitQuote] = useState(quote);
  const mq = useMediaQuery();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );
  const { data: eventTypeList } = useGetEventTypes(space?.id);

  const eventType = eventTypeList?.find(
    (eT) => eT.id === orderParams.eventTypeID
  )?.name;

  const fillAllParams = !!(
    orderParams.attendees &&
    orderParams.dateRange &&
    orderParams.eventTypeID &&
    orderParams.layoutID
  );

  const handleUpdateBooking = useCallback(() => {
    onUpdateQuote(true);
    setOpenUpdate(false);
    setInitialOrderParams(orderParams);
  }, [onUpdateQuote, orderParams]);

  const handleOpenDetailsBooking = useCallback(() => {
    setOpenUpdate(true);
    onUpdateQuote(false);
  }, [onUpdateQuote, setOpenUpdate]);

  const handleClosedUpdateBookingDetails = useCallback(() => {
    setOpenUpdate(false);
    onUpdateQuote(false);
    onResetOrderParams(initialOrderParams);
  }, [onUpdateQuote, setOpenUpdate, onResetOrderParams, initialOrderParams]);

  const handleChangePromoCode = useCallback(
    (code: string) => {
      setInitialOrderParams(orderParams);
      onChangePromoCode(code);
    },
    [onChangePromoCode, orderParams]
  );

  useEffect(() => {
    if (quote) {
      setInitQuote(quote);
    } else if (!!quoteError) {
      setOpenUpdate(true);
    }
  }, [quote, quoteError]);

  if (!initQuote) {
    return <PulseLoader />;
  }

  const handleChangeDateTime = (value?: DateTimeValue) => {
    const onlyDateSelected =
      isHourlyValue(value) && value.date && !value.startTime && !value.endTime;
    if (onlyDateSelected && value.date) {
      onSelectDate(parseISO(value.date));
    } else {
      onChangeDateRange(value);
    }
  };

  return (
    <>
      {space && location && (
        <>
          <Card
            className={mq.device === "mobile" ? "quote-view-mobile-card" : ""}
          >
            <ListGroup variant="flush">
              <ListGroup.Item>
                <SpaceView
                  offeringName={offeringName}
                  quote={initQuote}
                  onUpdateDetailsBooking={handleOpenDetailsBooking}
                  eventType={eventType}
                />
              </ListGroup.Item>
              {initQuote && (
                <>
                  <ListGroup.Item>
                    <QuoteDetails
                      quote={initQuote}
                      currency={space.currencyCode}
                    />
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <PromoCodeBox
                      partnerID={partnerID}
                      promotableType="Space"
                      promotableID={orderParams.spaceID}
                      email={orderParams.email}
                      timeRange={
                        isHourlyValue(orderParams.dateRange)
                          ? orderParams.dateRange
                          : undefined
                      }
                      dayPassDateRange={
                        isHourlyValue(orderParams.dateRange)
                          ? undefined
                          : orderParams.dateRange
                      }
                      quote={initQuote}
                      currency={space?.currencyCode}
                      onChangePromoCode={(code) => handleChangePromoCode(code)}
                    />
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <ListGroup>
                      <ListGroup.Item className="border-0">
                        <TotalPrice
                          total={initQuote.totalPrice}
                          currency={space.currencyCode}
                        />
                      </ListGroup.Item>
                    </ListGroup>
                  </ListGroup.Item>
                  {!orderParams.email && bookingPolicy?.checkoutWarning && (
                    <CheckoutWarning message={bookingPolicy.checkoutWarning} />
                  )}
                </>
              )}
            </ListGroup>
          </Card>
          <UpdateBookingDetails
            onClose={handleClosedUpdateBookingDetails}
            open={openUpdate}
            onUpdateBooking={handleUpdateBooking}
            offeringName={offeringName}
            space={space}
            scrollable
            disabled={!fillAllParams}
            checkoutDetails={
              <EventBookingContainer
                availableDays={availableDays}
                timeslots={timeslots}
                space={space}
                value={orderParams}
                disableRangeSelection={disableRangeSelection}
                onChangeDateTime={handleChangeDateTime}
                onChangeMonth={onNextVisibleDates}
                onChangeEventType={onChangeEventType}
                onChangeLayout={onChangeLayout}
                onChangeNumberOfGuests={onChangeNumberOfGuests}
              />
            }
          />
        </>
      )}
    </>
  );
}

interface QuoteViewDailyOfficeDesktopProps {
  quote: Quote | undefined;
  quoteError: string;
  capacities: number[];
  multiPasses: SpaceOffering[];
  capacityOptionLabel?: string;
  location: Location | null | undefined;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  orderParams: DailyOfficeOrderParams;
  onResetOrderParams: (orderParams: DailyOfficeOrderParams) => void;
  orderParamsErrors?: DailyOfficeOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onChangeCapacity: (qty: number) => void;
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue?: () => void;
  onUpdateQuote: (isUpdate: boolean) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  offeringName: string;
  partnerID: string;
  onChangePromoCode: (value: string) => void;
  datePickerPlaceholder?: string;
  promotableType: string;
  promotableID?: string;
  showDiscountLabel?: boolean;
  availableDays?: Date[];
  multiDayBookingAllowed: boolean;
}

export function QuoteViewDailyOfficeDesktop(
  props: QuoteViewDailyOfficeDesktopProps
) {
  const {
    quote,
    quoteError,
    location,
    capacities,
    multiPasses,
    orderParams,
    orderParamsErrors,
    onResetOrderParams,
    onUpdateQuote,
    locations,
    space,
    onChangeCity,
    onChangeLocation,
    onChangeCapacity,
    onChangeAddOnID,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    offeringName,
    datePickerPlaceholder,
    partnerID,
    onChangePromoCode,
    promotableType,
    promotableID,
    showDiscountLabel,
    availableDays,
    multiDayBookingAllowed,
  } = props;
  const [openUpdate, setOpenUpdate] = useState<boolean>(false);
  const [initialOrderParams, setInitialOrderParams] =
    useState<DailyOfficeOrderParams>(orderParams);
  const [initQuote, setInitQuote] = useState(quote);
  const mq = useMediaQuery();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const handleUpdateBooking = useCallback(() => {
    onUpdateQuote(true);
    setOpenUpdate(false);
    setInitialOrderParams(orderParams);
  }, [onUpdateQuote, orderParams]);

  const handleOpenDetailsBooking = useCallback(() => {
    setOpenUpdate(true);
    onUpdateQuote(false);
  }, [onUpdateQuote, setOpenUpdate]);

  const handleClosedUpdateBookingDetails = useCallback(() => {
    setOpenUpdate(false);
    onUpdateQuote(false);
    onResetOrderParams(initialOrderParams);
  }, [onUpdateQuote, setOpenUpdate, onResetOrderParams, initialOrderParams]);

  const handleChangePromoCode = useCallback(
    (code: string) => {
      setInitialOrderParams(orderParams);
      onChangePromoCode(code);
    },
    [onChangePromoCode, orderParams]
  );

  useEffect(() => {
    if (quote) {
      setInitQuote(quote);
    } else if (!!quoteError) {
      setOpenUpdate(true);
    }
  }, [quote, quoteError]);

  if (!initQuote) {
    return <PulseLoader />;
  }

  return (
    <>
      {space && location && (
        <>
          <Card
            className={mq.device === "mobile" ? "quote-view-mobile-card" : ""}
            data-testid="quote-view-daily-office-desktop-card"
          >
            <ListGroup variant="flush">
              <ListGroup.Item>
                {space && (
                  <SpaceView
                    offeringName={offeringName}
                    quote={initQuote}
                    onUpdateDetailsBooking={handleOpenDetailsBooking}
                  />
                )}
              </ListGroup.Item>
              {initQuote && (
                <>
                  <ListGroup.Item>
                    <QuoteDetails
                      quote={initQuote}
                      currency={space?.currencyCode}
                    />
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <PromoCodeBox
                      partnerID={partnerID}
                      email={orderParams.email}
                      quote={initQuote}
                      currency={space?.currencyCode}
                      onChangePromoCode={(code) => handleChangePromoCode(code)}
                      dayPassDateRange={
                        orderParams.type === "DailyOffice"
                          ? orderParams.dateRange
                          : undefined
                      }
                      quantity={
                        orderParams.type === "DailyOffice"
                          ? orderParams.capacity
                          : undefined
                      }
                      promotableType={promotableType}
                      promotableID={promotableID}
                    />
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <ListGroup>
                      <ListGroup.Item className="border-0">
                        <TotalPrice
                          total={initQuote.totalPrice}
                          currency={space.currencyCode}
                        />
                      </ListGroup.Item>
                    </ListGroup>
                  </ListGroup.Item>
                  {!orderParams.email && bookingPolicy?.checkoutWarning && (
                    <CheckoutWarning message={bookingPolicy.checkoutWarning} />
                  )}
                </>
              )}
            </ListGroup>
          </Card>
          <UpdateBookingDetails
            onClose={handleClosedUpdateBookingDetails}
            open={openUpdate}
            onUpdateBooking={handleUpdateBooking}
            offeringName={offeringName}
            space={space}
            checkoutDetails={
              <DailyOfficeCheckoutBoxDesktop
                orderParamsErrors={orderParamsErrors}
                firstDayOfWeek={firstDayOfWeek}
                orderParams={orderParams}
                multiPasses={multiPasses || []}
                isOutsideRange={isOutsideRange}
                isBlockedDate={isBlockedDate}
                onChangeAddOnID={onChangeAddOnID}
                onChangeLocation={onChangeLocation}
                onChangeCity={onChangeCity}
                onChangeDateRange={onChangeDateRange}
                onContinue={onContinue}
                onNextVisibleDates={onNextVisibleDates}
                locations={locations}
                location={location}
                quote={quote ? quote : undefined}
                space={space}
                showCTA={false}
                datePickerPlaceholder={datePickerPlaceholder}
                capacities={capacities}
                onChangeCapacity={onChangeCapacity}
                showDiscountLabel={showDiscountLabel}
                availableDays={availableDays}
                multiDayBookingAllowed={multiDayBookingAllowed}
              />
            }
          />
        </>
      )}
    </>
  );
}

interface QuoteViewMembershipDesktopProps {
  quote: Quote | undefined;
  locations: Location[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: MembershipOrderParams;
  onResetOrderParams: (orderParams: MembershipOrderParams) => void;
  orderParamsErrors?: MembershipOrderParamsErrors;
  onChangeLocation: (spaceID: string) => void;
  onChangeCity: (city: string) => void;
  onContinue?: () => void;
  onUpdateQuote: (isUpdate: boolean) => void;
  firstDayOfWeek?: FirstDayOfWeek;
  offeringName: string;
  partnerID: string;
  promotableType: string;
  promotableID?: string;
  email?: string;
  onChangePromoCode: (value: string) => void;
  showDiscountLabel?: boolean;
  offering: PartnerOffering;
  onChangeStartDate?: (startDate: string) => void;
  availableDays?: string[];
}

export function QuoteViewMembershipDesktopV2(
  props: QuoteViewMembershipDesktopProps
) {
  const {
    quote,
    location,
    orderParams,
    orderParamsErrors,
    onResetOrderParams,
    locations,
    space,
    onChangeLocation,
    onChangeCity,
    onContinue,
    onUpdateQuote,
    offeringName,
    partnerID,
    promotableType,
    promotableID,
    onChangePromoCode,
    showDiscountLabel,
    offering,
    onChangeStartDate,
    availableDays,
  } = props;

  const [openUpdate, setOpenUpdate] = useState<boolean>(false);
  const [initialOrderParams, setInitialOrderParams] =
    useState<MembershipOrderParams>(orderParams);
  const [initQuote, setInitQuote] = useState(quote);
  const mq = useMediaQuery();

  const selectedMembership = useMemo(
    () =>
      getMembershipInfoFromLocation(offering.offeringLocations, location?.id),
    [location, offering.offeringLocations]
  );

  const shouldUseOffering =
    !location && !shouldHideLocationAndCityPicker(offering.offeringLocations);

  const handleUpdateBooking = useCallback(() => {
    onUpdateQuote(true);
    setOpenUpdate(false);
    setInitialOrderParams(orderParams);
  }, [onUpdateQuote, orderParams]);

  const handleOpenDetailsBooking = useCallback(() => {
    setOpenUpdate(true);
    onUpdateQuote(false);
  }, [onUpdateQuote, setOpenUpdate]);

  const handleClosedUpdateBookingDetails = useCallback(() => {
    setOpenUpdate(false);
    onUpdateQuote(false);
    onResetOrderParams(initialOrderParams);
  }, [onUpdateQuote, setOpenUpdate, onResetOrderParams, initialOrderParams]);

  const handleChangePromoCode = useCallback(
    (code: string) => {
      setInitialOrderParams(orderParams);
      onChangePromoCode(code);
    },
    [onChangePromoCode, orderParams]
  );

  useEffect(() => {
    if (quote) {
      setInitQuote(quote);
    }
  }, [quote]);

  const canReselectLocation =
    (orderParams.locationID ||
      (!orderParams.locationID &&
        !shouldHideLocationAndCityPicker(offering.offeringLocations))) &&
    locations.length > 1;

  const canReselectStartDate = offering.allowSelectableStartDate;

  useEffect(() => {
    const shouldAutoSelectLocation =
      !orderParams.locationID &&
      !shouldHideLocationAndCityPicker(offering.offeringLocations);

    const availableLocation = getFirstAvailableLocation(
      locations.filter((loc) => loc.city === orderParams.city),
      offering.offeringLocations
    );
    if (
      !openUpdate &&
      shouldAutoSelectLocation &&
      orderParams.city &&
      availableLocation
    ) {
      onChangeLocation(availableLocation.id);
    }
  }, [
    locations,
    offering.offeringLocations,
    onChangeLocation,
    openUpdate,
    orderParams.city,
    orderParams.locationID,
  ]);

  const shouldShowUpdate = canReselectLocation || canReselectStartDate;

  if (!initQuote) {
    return <PulseLoader />;
  }

  const allFeeLines = getAllQuoteFeeLines(initQuote);

  return (
    <div
      className={mq.device === "mobile" ? "quote-view-membership-mobile" : ""}
    >
      <Card>
        <ListGroup variant="flush">
          <ListGroup.Item className="p-0">
            <div
              className={classNames("membership-list-item-space", {
                "membership-list-item-mobile-space": mq.device === "mobile",
              })}
            >
              <MembershipSpaceViewV2
                offeringName={offeringName}
                quote={initQuote}
                onUpdateDetailsBooking={
                  shouldShowUpdate ? handleOpenDetailsBooking : undefined
                }
                location={location}
                selectedMembership={selectedMembership}
                orderParams={orderParams}
                startDate={availableDays?.length ? availableDays[0] : undefined}
              />
            </div>
          </ListGroup.Item>
          {initQuote && (
            <>
              <ListGroup.Item className="p-0">
                <div
                  className={classNames("membership-list-item-space", {
                    "membership-list-item-mobile-space": mq.device === "mobile",
                  })}
                >
                  {shouldShowUpdate && (
                    <UpdateBookingDetails
                      onClose={handleClosedUpdateBookingDetails}
                      open={openUpdate}
                      offeringName={offeringName}
                      offering={offering}
                      onUpdateBooking={handleUpdateBooking}
                      space={space}
                      disabled={
                        (shouldHideLocationAndCityPicker(
                          offering.offeringLocations
                        ) ||
                          !!orderParams.locationID) &&
                        selectedMembership?.inventory === 0
                      }
                      membershipImages={
                        shouldUseOffering ? [] : selectedMembership?.images
                      }
                      checkoutDetails={
                        <MembershipCheckoutBoxDesktopV2
                          quoteLoading={false}
                          selectedMembership={selectedMembership}
                          orderParamsErrors={orderParamsErrors}
                          orderParams={orderParams}
                          onChangeLocation={onChangeLocation}
                          onChangeCity={onChangeCity}
                          onContinue={onContinue}
                          locations={locations}
                          location={location}
                          quote={quote ? quote : undefined}
                          showCTA={false}
                          showDiscountLabel={showDiscountLabel}
                          onChangeStartDate={onChangeStartDate}
                          showStartDate={offering.allowSelectableStartDate}
                          offeringLocations={offering.offeringLocations}
                          soldOut={
                            !shouldHideLocationAndCityPicker(
                              offering.offeringLocations
                            ) && selectedMembership?.inventory === 0
                          }
                        />
                      }
                    />
                  )}

                  {selectedMembership && (
                    <MembershipPriceInfo
                      firstMonthAdjustment={
                        initQuote.quoteItems[0].unitPrice -
                        initQuote.paymentIntent?.proratedAmount
                      }
                      monthlyPrice={initQuote.quoteItems[0].unitPrice}
                      currency={initQuote.currency}
                      promotion={initQuote.promotionLine}
                      taxLine={initQuote.taxLine}
                      paymentCycles={selectedMembership.firstPaymentCycles}
                      feeLines={allFeeLines}
                    />
                  )}
                </div>
              </ListGroup.Item>

              <ListGroup.Item
                className={classNames("membership-list-item-promo", {
                  "membership-list-item-mobile-promo": mq.device === "mobile",
                })}
              >
                <PromoCodeBox
                  partnerID={partnerID}
                  promotableType={promotableType}
                  promotableID={promotableID}
                  email={orderParams.email}
                  quote={initQuote}
                  currency={selectedMembership?.currency}
                  onChangePromoCode={(code) => handleChangePromoCode(code)}
                />
              </ListGroup.Item>

              <ListGroup.Item className="p-0">
                <div
                  data-testid="membership-pricing-wrapper"
                  className={classNames("membership-list-item-steps", {
                    "membership-list-item-mobile-steps": mq.device === "mobile",
                  })}
                >
                  <MembershipCheckoutPriceV2
                    startDate={orderParams.subscriptionStartDate}
                    todayPrice={initQuote.totalPrice}
                    recurringPrice={
                      initQuote.quoteItems[0].unitPriceWithTaxAndFee
                    }
                    recurringPriceWithPromoIteration={
                      initQuote.quoteItems[0]
                        .unitPriceAfterDiscountWithTaxAndFee
                    }
                    currency={initQuote.currency}
                    selectedMembership={selectedMembership}
                    promoLineItem={initQuote.promotionLine}
                  />
                </div>
              </ListGroup.Item>
            </>
          )}
        </ListGroup>
      </Card>
    </div>
  );
}

function CheckoutWarning(props: { message: string }) {
  return (
    <ListGroup.Item>
      <ListGroup>
        <ListGroup.Item className="border-0">
          <span>{props.message}</span>
        </ListGroup.Item>
      </ListGroup>
    </ListGroup.Item>
  );
}

interface SpaceBookingInfoProps {
  offeringName: string;
  quote: Quote;
  eventType?: string;
}

function SpaceBookingInfo({
  offeringName,
  quote,
  eventType,
}: SpaceBookingInfoProps) {
  const orderItem = quote.orderItems[0];
  const spaceID = getFieldFromObject(
    "spaceID",
    getFieldFromObject("details", orderItem)
  );
  const quotedSpace = useSpaceMaybeQuery(spaceID);
  const details = getFieldFromObject("details", orderItem);
  const type = getFieldFromObject("type", details);

  const isEventSpace = quotedSpace?.spaceType === "event_space";
  const isDayPass = quotedSpace?.spaceType === "entire_location";
  const isDayOffice = quotedSpace?.spaceType === "private_office";
  const isMultiPass = orderItem.type === OrderableType.AddOn;

  let quantity = orderItem.quantity;

  const layout = quotedSpace?.layouts[0];
  if (isDayOffice && layout) {
    quantity = layout.capacity;
  }

  const layoutName = quotedSpace?.layouts.find(
    (l) => l.id === details?.layoutID
  )?.name;

  const quoteItemPrices: DayUnitQuoteItemPrice[] = [];
  quote.quoteItems[0]?.quoteItemPrices.forEach((item) => {
    if (item.unitType === "day_unit") {
      quoteItemPrices.push(item);
    }
  });
  const bookingDates = quoteItemPrices.map((price) => price.startDate);

  return (
    <div>
      <div className="row g-0">
        <h3 className="card-title">{offeringName}</h3>
      </div>
      <div className="row g-0 mt-2">
        {quotedSpace && (
          <SpaceImage
            image={quotedSpace?.images[0].small.url}
            title={offeringName}
          />
        )}
        <div data-testid="space-view-text-info" className="col-7 col-md-7">
          <div className="card-body pt-0">
            <BookingAddress streetAddress={quotedSpace?.streetAddress} />
            {type === OrderableType.Membership ? null : type ===
              BookingType.Hourly ? (
              <BookingDateTime
                dateRange={{
                  date: getFieldFromObject("date", details),
                  startTime: getFieldFromObject("startTime", details),
                  endTime: getFieldFromObject("endTime", details),
                }}
              />
            ) : (
              type === BookingType.Daily && (
                <BookingDateRange
                  startDate={getFieldFromObject("startDate", details)}
                  endDate={getFieldFromObject("endDate", details)}
                  bookingDates={bookingDates}
                />
              )
            )}
            {isEventSpace && eventType && (
              <BookingEventType eventType={eventType} />
            )}
            {isEventSpace && layoutName && (
              <BookingLayout layoutName={layoutName} />
            )}
            {isMultiPass && (
              <BookingAddOnName addOnName={orderItem.details.name} />
            )}
            {!isMultiPass && (isDayOffice || isDayPass) ? (
              <BookingCapacity capacity={quantity} />
            ) : (
              !!details?.attendees && (
                <BookingCapacity capacity={details?.attendees} />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SpaceViewProps {
  offeringName: string;
  quote?: Quote;
  onUpdateDetailsBooking: () => void;
  testId?: string;
  eventType?: string;
}

function SpaceView(props: SpaceViewProps) {
  const { offeringName, quote, onUpdateDetailsBooking, testId, eventType } =
    props;
  const { fbt } = useI18n();

  const orderItem = quote?.orderItems[0];
  if (!orderItem) {
    return <></>;
  }

  return (
    <div className="card border-0" style={{ padding: 16 }} data-testid={testId}>
      <SpaceBookingInfo
        offeringName={offeringName}
        quote={quote}
        eventType={eventType}
      />
      <div className="row g-0 pt-3">
        <Button
          variant="outline-primary"
          style={{ width: "100%" }}
          onClick={onUpdateDetailsBooking}
          data-testid={"update-booking-details-button"}
        >
          {fbt("Update")}
        </Button>
      </div>
    </div>
  );
}

interface MembershipSpaceViewProps {
  offeringName: string;
  quote?: Quote;
  onUpdateDetailsBooking?: () => void;
  attendees?: number;
  location: Location | null | undefined;
  selectedMembership?: MembershipDetail;
  order?: Order;
  orderParams?: MembershipOrderParams;
  startDate?: string;
}

function MembershipSpaceViewV2(props: MembershipSpaceViewProps) {
  const {
    offeringName,
    quote,
    location,
    selectedMembership,
    order,
    onUpdateDetailsBooking,
    orderParams,
    startDate,
  } = props;

  const { fbt } = useI18n();

  const { subscriptionStartDate } = orderParams || {};

  const subsStartDate = subscriptionStartDate || startDate;

  const orderItem = quote?.orderItems[0];
  if (!orderItem) {
    return <></>;
  }

  const promoItem = quote.promotionLine;

  const promoMonthsLeftAfterFirstPayment = getPromoMonthsLeftAfterFirstPayment({
    subscriptionIterations: promoItem?.subscriptionIterations,
    firstPaymentCycles: selectedMembership?.firstPaymentCycles,
  });

  return (
    <div className="card border-0">
      <div className="row g-0">
        <h3 className="card-title">{offeringName}</h3>
      </div>

      <div className="row g-0 mt-2">
        <div className="membership-quote-basics">
          {!!location && <MembershipBookingAddressV2 loc={location} />}

          <div className="membership-quote-info">
            <Icon name="calendar" size={16} />
            <div>
              <div className="membership-quote-basics-title">
                <span>{fbt("Membership starts:")}</span>
              </div>
              <div>
                {format(
                  subsStartDate
                    ? parseDay(subsStartDate)
                    : order?.createdAt || parseDay(toDay(new Date())),
                  "MMMM d, yyyy"
                )}
              </div>
            </div>
          </div>
          {selectedMembership?.recurring && (
            <div className="membership-quote-info">
              <Icon name="bill" />
              <div>
                <div className="membership-quote-basics-title">
                  <span>{fbt("Next billing date:")}</span>
                </div>
                <div>
                  {getFirstDayOfNextMonthAfterNMonths({
                    addedMonths:
                      (selectedMembership?.firstPaymentCycles || 0) +
                      monthDifference(
                        new Date(),
                        subsStartDate
                          ? parseDay(subsStartDate)
                          : startDate
                          ? parseDay(startDate)
                          : new Date()
                      ) +
                      (quote.totalPrice === 0 &&
                      promoMonthsLeftAfterFirstPayment > 0
                        ? promoMonthsLeftAfterFirstPayment
                        : 0),
                    formatString: "MMMM d, yyyy",
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {!!location && onUpdateDetailsBooking && (
        <div className="row g-0 pt-3">
          <Button
            variant="outline-primary"
            style={{ width: "100%" }}
            onClick={onUpdateDetailsBooking}
          >
            {fbt("Update")}
          </Button>
        </div>
      )}
    </div>
  );
}

function SpaceImage(props: { image: string; title: string }) {
  return (
    <div className="col-5 col-md-5">
      <img
        src={props.image}
        alt={props.title}
        className="img-fluid rounded-start"
        style={{
          objectFit: "cover",
          height: 141,
          width: 141,
          borderRadius: 5,
        }}
      />
    </div>
  );
}

function BookingDateTime(props: { dateRange: MeetingRoomDateRange }) {
  const { fbt, locale } = useI18n();

  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-datetime"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/calendar-icon.svg"
            style={{ width: 16 }}
          />
        </div>
        <span className="col-10 ps-0">
          {formatMeetingRoomDateRange(props.dateRange, fbt, locale, "")}
        </span>
      </small>
    </div>
  );
}

function BookingDateRange(props: {
  startDate: string;
  endDate: string;
  bookingDates?: string[];
}) {
  const { fbt, locale } = useI18n();

  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-date-range"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/calendar-icon.svg"
            style={{ width: 16 }}
          />
        </div>
        <span className="col-10 ps-0">
          {formatDayRange(
            {
              start: props.startDate,
              end: props.endDate,
            },
            fbt,
            locale,
            undefined,
            props.bookingDates?.map((d) => parseISO(d))
          )}
        </span>
      </small>
    </div>
  );
}

function BookingCapacity(props: { capacity?: number }) {
  const { fbt } = useI18n();

  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-datetime"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/guest-icon.svg"
            style={{ width: 16 }}
          />
        </div>
        <span className="col-10 ps-0">
          {props.capacity && props.capacity > 1
            ? `${props.capacity + " " + fbt("Guests")}`
            : `${props.capacity + " " + fbt("Guest")}`}
        </span>
      </small>
    </div>
  );
}

function BookingEventType(props: { eventType: string }) {
  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-datetime"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/event-type.svg"
            style={{ width: 16 }}
          />
        </div>
        <span className="col-10 ps-0">{props.eventType}</span>
      </small>
    </div>
  );
}

function BookingLayout(props: { layoutName: string }) {
  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-datetime"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/layout.svg"
            style={{ width: 16 }}
          />
        </div>
        <span className="col-10 ps-0">{props.layoutName} Layout</span>
      </small>
    </div>
  );
}

function BookingAddOnName(props: { addOnName?: string }) {
  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-addon"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/multi-pass.svg"
            style={{ width: 24 }}
          />
        </div>
        <span className="col-10 ps-0">{props.addOnName}</span>
      </small>
    </div>
  );
}

function BookingAddress(props: { streetAddress?: string }) {
  return (
    <div className="card-text pb-2">
      <small className="text-muted row">
        <div className="col-2 space-view-icon">
          <img
            alt="booking-address"
            src="https://meet-production.s3.us-west-1.amazonaws.com/icons/offerings/location-icon.svg"
            style={{ width: 24 }}
          />
        </div>
        <span className="col-10 ps-0">{props.streetAddress}</span>
      </small>
    </div>
  );
}

function MembershipBookingAddressV2(props: { loc?: Location }) {
  const loc = props.loc;
  const { fbt } = useI18n();

  return (
    <div>
      {!!loc && (
        <div className="membership-quote-info">
          <div style={{ width: "16px", display: "flex" }}>
            <Icon name="map-pin" size={16} />
          </div>
          <div>
            <div className="membership-quote-basics-title">
              {fbt("Home location:")}
            </div>
            <span>{loc.name}</span>
            <div>{loc?.streetAddress}</div>
            <div>
              {loc?.city}, {loc?.state}, {loc?.country}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TotalPrice(props: {
  currency: string | null;
  total: number;
  orderStatus?: string;
  message?: string;
}) {
  const { fbt } = useI18n();

  return (
    <div className="row g-0">
      <h5 className="col-6 col-sm-6 col-md-6">
        {`${fbt("Total")} (${props.currency})`}
      </h5>
      <div className="col-6 col-sm-6 col-md-6 text-end">
        <h3 data-testid="total-price" className="mb-0">
          {props.currency && fbt.formatCurrency(props.total, props.currency)}
        </h3>
        {props.orderStatus !== OrderStatus.Canceled && props.message && (
          <span className="quote-view__cancellation_message">
            {props.message}
          </span>
        )}
      </div>
    </div>
  );
}

interface UpdateBookingDetailsProps {
  open: boolean;
  onClose: () => void;
  onUpdateBooking: () => void;
  space: Space | null | undefined;
  offeringName: string;
  checkoutDetails: React.ReactNode;
  offering?: PartnerOffering;
  membershipImages?: ImageVersion[];
  disabled?: boolean;
  scrollable?: boolean;
}

function UpdateBookingDetails(props: UpdateBookingDetailsProps) {
  const {
    open,
    onClose,
    space,
    offeringName,
    checkoutDetails,
    onUpdateBooking,
    offering,
    membershipImages,
    disabled,
    scrollable,
  } = props;
  const { fbt } = useI18n();
  const mq = useMediaQuery();

  const images = useMembershipStandardizeImages(
    membershipImages || [],
    offering
  );

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="lg"
      fullscreen="sm-down"
      centered
      aria-labelledby="contained-modal-title-vcenter"
      className="quote-view-modal header-center"
      data-testid="update-booking-details-modal"
      scrollable={scrollable}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">{fbt("Update booking details")}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: mq.device !== "mobile" ? 600 : undefined }}
        className={`show-grid ${
          mq.device === "mobile" ? "" : "update-booking-details-dialog__body"
        } `}
      >
        <BootstrapContainer>
          <Row>
            <Col
              xs={12}
              md={6}
              className="update-booking-details-dialog__image"
            >
              <img
                src={space?.images[0].small.url || images?.imagesMobile[0]?.url}
                className="img-fluid rounded-start"
                alt={space?.name}
                style={{
                  height: 259,
                  objectFit: "cover",
                  borderRadius: 5,
                  marginBottom: 16,
                }}
              />
            </Col>
            <Col
              xs={12}
              md={6}
              className="update-booking-details-dialog__checkout"
            >
              <h5>{offeringName}</h5>
              {checkoutDetails}
              <Button
                disabled={disabled}
                variant="primary"
                onClick={onUpdateBooking}
                style={{ width: "100%", marginTop: 33 }}
                className="btn-block"
                data-testid="update-booking-details-button"
              >
                {fbt("Update")}
              </Button>
            </Col>
          </Row>
        </BootstrapContainer>
      </Modal.Body>
    </Modal>
  );
}

interface QuoteDetailsProps {
  quote: Quote;
  currency: string | null;
}

export function QuoteDetails(props: QuoteDetailsProps) {
  const { quote, currency } = props;
  const { fbt } = useI18n();

  const quoteItems = quote.quoteItems;
  const [dialogOpen, setDialogOpen] = useState(false);

  const openPriceBreak = () => setDialogOpen(true);

  const allFeeLines = getAllQuoteFeeLines(quote);

  return (
    <ListGroup>
      {quoteItems &&
        quoteItems.map((quoteItem: QuoteItem) => {
          const quoteItemPrices = quoteItem.quoteItemPrices;

          const isHourlyBooking = !!(
            quoteItem.date &&
            quoteItem.startTime &&
            quoteItem.endTime
          );

          return (
            <>
              <QuoteItemLabel
                openPriceBreak={openPriceBreak}
                quoteItem={quoteItem}
                currency={currency}
              />

              {quoteItemPrices.length > 1 && (
                <Modal
                  show={dialogOpen}
                  onHide={() => setDialogOpen(false)}
                  size="lg"
                  fullscreen="sm-down"
                  centered
                  aria-labelledby="contained-modal-title-vcenter"
                  className="quote-view-modal header-center"
                >
                  <div data-testid="base-price-breakdown-dialog">
                    <Modal.Header closeButton>
                      <Modal.Title as="h5">
                        {fbt("Base price breakdown")}
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <BootstrapContainer>
                        {quoteItemPrices.map((quoteItemPrice) => (
                          <PriceBreakDownItem
                            quoteItemPrice={quoteItemPrice}
                            currency={currency}
                            quantity={quoteItem.quantity}
                          />
                        ))}
                        <Spacer size={4} />
                        <Divider />
                        <Spacer size={4} />
                        <div className="row fw-bold">
                          <span className="col-8">
                            {fbt("Total base price")}
                          </span>
                          <span className="col-4 text-end">
                            {fbt.formatCurrency(
                              Math.abs(
                                isHourlyBooking
                                  ? quoteItem.originalTotalPrice
                                  : quoteItem.subTotal
                              ),
                              currency
                            )}
                          </span>
                        </div>
                      </BootstrapContainer>
                    </Modal.Body>
                  </div>
                </Modal>
              )}
            </>
          );
        })}

      <ListGroup.Item className="py-2 border-0">
        <div className="row g-0">
          <span className="col-8 col-sm-8 col-md-8 fw-bold">
            {fbt("Subtotal")}
          </span>
          <span className="col-4 col-sm-4 col-md-4 text-end fw-bold">
            {fbt.formatCurrency(quote.subTotalBeforeDiscount, currency)}
          </span>
        </div>
      </ListGroup.Item>
      {!!allFeeLines.length && (
        <ListGroup.Item className="py-2 border-0">
          <AdditionalFeesItem currency={currency} feeLines={allFeeLines} />
        </ListGroup.Item>
      )}
      {quote.promotionLine && (
        <ListGroup.Item className="py-2 border-0">
          <div className="row g-0">
            <span className="col-8 col-sm-8 col-md-8 text-primary promo_code_description">
              {quote.promotionLine.description}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-primary text-end promo_code_amount">
              -{" "}
              {fbt.formatCurrency(
                Math.abs(quote.promotionLine.amount),
                currency
              )}
            </span>
          </div>
        </ListGroup.Item>
      )}
      {quote.taxLine && (
        <ListGroup.Item className="py-2 border-0">
          <div className="row g-0">
            <span className="col-8 col-sm-8 col-md-8">
              {quote.taxLine.description}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-end">
              {fbt.formatCurrency(quote.taxLine.amount, currency)}
            </span>
          </div>
        </ListGroup.Item>
      )}
    </ListGroup>
  );
}

function QuoteItemLabel(props: {
  quoteItem: QuoteItem;
  currency: string | null;
  openPriceBreak?: () => void;
}) {
  const { fbt } = useI18n();
  const [popoverOpen, { toggle }] = useToggle();
  const { quoteItem, currency, openPriceBreak } = props;

  const priceBeakdownPerHourFeatureFlag = usePriceBreakdownPerHourFeatureFlag();

  const space = quoteItem.orderable;
  const quoteItemPrices = quoteItem.quoteItemPrices;
  const unitCountPerQuantity = quoteItem.unitCount / quoteItem.quantity;
  const hasDynamicPricing = quoteItem.originalUnitPrice !== quoteItem.unitPrice;

  const iconWithToolTip = (
    <div className="d-flex">
      <Spacer size={4} direction="horizontal" />
      <Hoverable
        onHoverIn={() => toggle()}
        onHoverOut={() => toggle()}
        useWrapper
      >
        <Popover
          isOpen={popoverOpen}
          onRequestClose={toggle}
          content={
            <Container
              backgroundColor="white"
              minWidth={264}
              padding={16}
              boxShadow={cssVariables.shadow}
              borderWidth={1}
              borderColor={cssVariables.borderColorDefault}
              borderRadius={cssVariables.radius}
            >
              <span className="text-dark dynamic-pricing-popover-text">
                {fbt(
                  "We’ve chosen the best possible price for you based on your selections"
                )}
              </span>
            </Container>
          }
        >
          {({ ref }) => (
            <div ref={ref as any}>
              <Icon name="info" size={16} color="#06001E" />
            </div>
          )}
        </Popover>
      </Hoverable>
    </div>
  );

  const isMultiPass = quoteItem.orderable.numberOfEntries > 1;
  const isDailyBooking = !!(quoteItem.startDate && quoteItem.endDate);
  const isHourlyBooking = !!(
    quoteItem.date &&
    quoteItem.startTime &&
    quoteItem.endTime
  );

  if (isMultiPass) {
    const unitPrice =
      quoteItem.originalTotalPrice / quoteItem.orderable.numberOfEntries;

    return (
      <ListGroup.Item className="py-2 border-0">
        <div className="row g-0">
          <span className="col-8 col-sm-8 col-md-8 d-flex">
            <div>
              {space.name} ({fbt.formatCurrency(unitPrice, currency)}/
              {fbt("day")})
              {unitCountPerQuantity > 1 && (
                <>
                  {" x "}
                  {unitCountPerQuantity} {fbt("days")}
                </>
              )}
              {quoteItem.quantity > 1 && (
                <>
                  {" x "}
                  {quoteItem.quantity}
                </>
              )}
            </div>
          </span>
          <span className="col-4 col-sm-4 col-md-4 text-end">
            {fbt.formatCurrency(
              Math.abs(quoteItem.originalTotalPrice),
              currency
            )}
          </span>
        </div>
      </ListGroup.Item>
    );
  }

  if (isDailyBooking) {
    return (
      <ListGroup.Item className="py-2 border-0">
        <div className="row g-0">
          <span className="col-8 col-sm-8 col-md-8 d-flex">
            <div
              onClick={openPriceBreak ? openPriceBreak : undefined}
              role={quoteItemPrices.length > 1 ? "button" : undefined}
              className={
                quoteItemPrices.length > 1 ? "text-decoration-underline" : ""
              }
              data-testid="day-range-checkout-order-item"
            >
              {fbt.formatCurrency(quoteItem.unitPrice, props.currency)}
              {" x "}
              {unitCountPerQuantity}{" "}
              {fbt(pluralize("day", unitCountPerQuantity))}
              {quoteItem.quantity > 1 && (
                <>
                  {" x "}
                  {quoteItem.quantity}{" "}
                  {pluralize("guest", quoteItem.quantity || 1)}
                </>
              )}
            </div>
            {hasDynamicPricing && iconWithToolTip}
          </span>
          <span className="col-4 col-sm-4 col-md-4 text-end">
            {fbt.formatCurrency(Math.abs(quoteItem.subTotal), currency)}
          </span>
        </div>
      </ListGroup.Item>
    );
  }

  if (isHourlyBooking) {
    const isBetterRate =
      quoteItem.dynamicPriceRate &&
      quoteItem.dynamicPriceRate.adjustmentValue < 0;
    const isHigherDynamicPricing =
      quoteItem.dynamicPriceRate &&
      quoteItem.dynamicPriceRate.adjustmentValue > 0;
    const unitPrice = isHigherDynamicPricing
      ? quoteItem.unitPrice
      : quoteItem.originalUnitPrice;
    const total = isHigherDynamicPricing
      ? quoteItem.totalPrice
      : quoteItem.originalTotalPrice;

    return (
      <>
        <ListGroup.Item className="py-2 border-0">
          <div className="row g-0">
            <span className="col-8 col-sm-8 col-md-8 d-flex">
              <div
                onClick={
                  priceBeakdownPerHourFeatureFlag ? openPriceBreak : undefined
                }
                role={
                  priceBeakdownPerHourFeatureFlag && quoteItemPrices.length > 1
                    ? "button"
                    : undefined
                }
                className={
                  priceBeakdownPerHourFeatureFlag && quoteItemPrices.length > 1
                    ? "text-decoration-underline"
                    : undefined
                }
              >
                {fbt.formatCurrency(unitPrice, currency)}
                {" x "}
                {unitCountPerQuantity}{" "}
                {fbt(pluralize("hour", unitCountPerQuantity))}
              </div>
              {hasDynamicPricing && iconWithToolTip}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-end">
              {fbt.formatCurrency(Math.abs(total), currency)}
            </span>
          </div>
        </ListGroup.Item>
        {isBetterRate && (
          <ListGroup.Item className="py-2 border-0">
            <div className="row g-0 quote_item_price_label__discount">
              <span className="col-8 col-sm-8 col-md-8">
                {quoteItem.dynamicPriceRate?.description}
              </span>
              <span className="col-4 col-sm-4 col-md-4 text-end">
                -
                {fbt.formatCurrency(
                  Math.abs(
                    Number(
                      (
                        quoteItem.originalTotalPrice - quoteItem.subTotal
                      ).toFixed(2)
                    )
                  ),
                  currency
                )}
              </span>
            </div>
          </ListGroup.Item>
        )}
      </>
    );
  }

  return (
    <ListGroup.Item className="py-2 border-0 fw-semibold">
      <div className="row g-0">
        <span className="col-8 col-sm-8 col-md-8">
          {quoteItem.orderable.displayName}
        </span>
        <span className="col-4 col-sm-4 col-md-4 text-end">
          {fbt.formatCurrency(Math.abs(quoteItem.originalTotalPrice), currency)}
        </span>
      </div>
    </ListGroup.Item>
  );
}

function PriceBreakDownItem(props: {
  quoteItemPrice: QuoteItemPrice;
  currency: string | null;
  quantity: number;
}) {
  const { quoteItemPrice, currency, quantity } = props;
  const isBetterRate =
    quoteItemPrice.dynamicPriceRate &&
    quoteItemPrice.dynamicPriceRate.adjustmentValue < 0;

  const { fbt } = useI18n();

  return (
    <ListGroup.Item className="py-1 border-0">
      <div
        className={classNames(
          "row",
          "g-0",
          isBetterRate && "quote_item_price_label__discount"
        )}
      >
        {quoteItemPrice.unitType === "day_unit" ? (
          <>
            <span className="col-8 col-sm-8 col-md-8">
              {fbt.formatDateByPattern(
                parseISO(quoteItemPrice.startDate),
                "MMMM dd, yyyy"
              )}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-end">
              {fbt.formatCurrency(Math.abs(quoteItemPrice.unitPrice), currency)}
              {quantity > 1 && (
                <span>
                  {" "}
                  x {quantity} {fbt(pluralize("guest", quantity))}
                </span>
              )}
            </span>
          </>
        ) : quoteItemPrice.unitType === "hour_unit" ? (
          <>
            <span className="col-8 col-sm-8 col-md-8">
              {fbt.formatTime(parseTime(quoteItemPrice.startTime))} -{" "}
              {fbt.formatTime(parseTime(quoteItemPrice.endTime))}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-end">
              {fbt.formatCurrency(
                Math.abs(quoteItemPrice.totalPrice),
                currency
              )}
            </span>
          </>
        ) : null}
      </div>
    </ListGroup.Item>
  );
}

interface PromoCodeBoxProps {
  partnerID: string;
  promotableType: string;
  promotableID?: string;
  email?: string;
  quantity?: number;
  dayPassDateRange?: DayPassDateRange;
  timeRange?: MeetingRoomDateRange;
  quote: Quote;
  currency?: string | null;
  onChangePromoCode: (value: string) => void;
}

function PromoCodeBox(props: PromoCodeBoxProps) {
  const {
    partnerID,
    promotableType,
    promotableID,
    quantity,
    email,
    dayPassDateRange,
    timeRange,
    onChangePromoCode,
    currency,
    quote,
  } = props;
  const { fbt } = useI18n();
  const validatePromo = useValidatePromoQuery();
  const { promoCode } = useQueryString<{ promoCode?: string }>();

  const { setFieldValue, values, submitForm, setFieldError, errors } =
    useFormik({
      initialValues: {
        code: "",
      },
      onSubmit: async (v, { setSubmitting }) => {
        onChangePromoCode(v.code.trim());
        setSubmitting(false);
      },
      validateOnChange: false,
      validateOnBlur: false,
      validateOnMount: true,
      validate: async (v) => {
        if (promotableID && v.code.trim().length) {
          const result = await validatePromo(
            encodeURIComponent(v.code.trim()),
            partnerID,
            promotableType,
            promotableID,
            email,
            dayPassDateRange?.startDate,
            dayPassDateRange?.endDate,
            timeRange?.date,
            timeRange?.startTime,
            timeRange?.endTime,
            quantity
          );

          if (result.errors.length) {
            return {
              code: result.errors[0],
            };
          }
        }
      },
    });

  useEffect(() => {
    (async () => {
      if (promotableID && promoCode?.trim().length) {
        const result = await validatePromo(
          encodeURIComponent(promoCode.trim()),
          partnerID,
          promotableType,
          promotableID,
          email,
          dayPassDateRange?.startDate,
          dayPassDateRange?.endDate,
          timeRange?.date,
          timeRange?.startTime,
          timeRange?.endTime,
          quantity
        );
        if (result.valid) {
          setFieldValue("code", promoCode);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPromoCode = useCallback(() => {
    onChangePromoCode("");
    setFieldValue("code", "");
    setFieldError("code", undefined);
  }, [onChangePromoCode, setFieldValue, setFieldError]);

  return (
    <ListGroup>
      <ListGroup.Item className="border-0 py-2">
        <span className="text-uppercase">{fbt("Promo code")}</span>
      </ListGroup.Item>
      {quote.promotionLine ? (
        <ListGroup.Item className="border-0 py-2">
          <div className="row g-0">
            <span
              style={{ cursor: "pointer" }}
              className="col-1 col-sm-1 col-md-1"
              onClick={clearPromoCode}
            >
              <Icon name="x" size={16} color="red" />
            </span>
            <span className="col-7 col-sm-7 col-md-7 text-primary promo_code_text">
              {quote.promotionLine.code}
            </span>
            <span className="col-4 col-sm-4 col-md-4 text-primary text-end promo_code_amount">
              {currency &&
                fbt.formatCurrency(
                  Math.abs(quote.promotionLine.amount),
                  currency
                )}
            </span>
          </div>
        </ListGroup.Item>
      ) : (
        <ListGroup.Item className="border-0 py-2">
          <div className="input-group">
            <input
              onChange={(e) => {
                if (e.target.value) {
                  setFieldValue("code", e.target.value);
                } else {
                  setFieldValue("code", "");
                  setFieldError("code", undefined);
                }
              }}
              value={values.code}
              type="text"
              name="code"
              style={{ height: 50 }}
              className={`promo_code_box__input form-control ${
                errors.code ? "is-invalid" : ""
              }`}
              placeholder={`${fbt("Enter a code")}`}
              data-testid="promo-code-input"
            />
            <button
              onClick={submitForm}
              disabled={!values.code}
              className="promo_code_box__button btn btn-outline-primary px-5"
              type="submit"
              data-testid="apply-promo-code-button"
            >
              {fbt("Apply")}
            </button>
            {errors && <div className="invalid-feedback">{errors.code}</div>}
          </div>
        </ListGroup.Item>
      )}
    </ListGroup>
  );
}

interface OrderSummaryProps {
  orderItem: OrderItem | undefined;
  location: Location;
  order: Order;
  space: Space;
  currency: string | null;
  offeringName: string;
}

export function OrderSummary(props: OrderSummaryProps) {
  const { orderItem, location, space, offeringName, order, currency } = props;
  const { locale, fbt } = useI18n();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    space.id,
    orderItem?.type === OrderableType.Booking
      ? orderItem.details?.type === BookingType.Daily
        ? orderItem.details.startDate
        : orderItem.details?.type === BookingType.Hourly
        ? orderItem.details.date
        : undefined
      : undefined,
    orderItem?.type === OrderableType.Booking
      ? orderItem.details?.type === BookingType.Daily
        ? orderItem.details.endDate
        : orderItem.details?.type === BookingType.Hourly
        ? orderItem.details.date
        : undefined
      : undefined
  );
  const orderParams = orderItemToOrderParams(orderItem);
  const quote = useQuoteV2Query(order.quoteId);
  const cancellationPolicy = getCheckoutCancellationPolicyText(
    fbt,
    location.timezone,
    bookingPolicy,
    orderParams,
    locale,
    quote
  );
  const { eventTypeID } = useQueryString<{ eventTypeID: string }>();
  const { data: eventTypeList } = useGetEventTypes(space.id);

  const eventType = eventTypeList?.find((eT) => eT.id === eventTypeID)?.name;

  return (
    <Card style={{ borderColor: colors.blackMinus70 }}>
      <ListGroup variant="flush">
        <ListGroup.Item style={{ borderColor: colors.blackMinus70 }}>
          {space && orderItem && (
            <SpaceBookingInfo
              offeringName={offeringName}
              quote={quote}
              eventType={eventType}
            />
          )}
        </ListGroup.Item>
        {order && (
          <>
            <ListGroup.Item style={{ borderColor: colors.blackMinus70 }}>
              <QuoteDetails quote={quote} currency={currency} />
            </ListGroup.Item>
            <ListGroup.Item style={{ borderColor: colors.blackMinus70 }}>
              <ListGroup>
                <ListGroup.Item className="border-0">
                  <TotalPrice
                    total={order.totalPrice}
                    currency={currency}
                    orderStatus={order.status}
                    message={cancellationPolicy}
                  />
                </ListGroup.Item>
              </ListGroup>
            </ListGroup.Item>
          </>
        )}
      </ListGroup>
    </Card>
  );
}

interface MembershipOrderSummaryProps {
  location?: Location;
  order: Order;
  offeringName: string;
  selectedMembership?: MembershipDetail;
  subscriptionStartDate?: string;
}

export function MembershipOrderSummary(props: MembershipOrderSummaryProps) {
  const {
    offeringName,
    order,
    location,
    selectedMembership,
    subscriptionStartDate,
  } = props;
  const quote = useQuoteV2Query(order.quoteId);
  const mq = useMediaQuery();

  const { data: availableDays } = useGetAvailableDaysMembership(
    location ? selectedMembership?.id : undefined
  );

  const allFeeLines = getAllQuoteFeeLines(quote);

  return (
    <Card>
      <ListGroup variant="flush">
        {quote && order && (
          <>
            <ListGroup.Item className="p-0">
              <div
                style={{ padding: mq.device === "mobile" ? "16px" : "32px" }}
              >
                <MembershipSpaceViewV2
                  offeringName={offeringName}
                  quote={quote}
                  location={location}
                  selectedMembership={selectedMembership}
                  startDate={
                    subscriptionStartDate
                      ? subscriptionStartDate
                      : availableDays?.length
                      ? availableDays[0]
                      : undefined
                  }
                />
              </div>
            </ListGroup.Item>

            {selectedMembership && (
              <ListGroup.Item className="p-0">
                <div
                  style={{ padding: mq.device === "mobile" ? "16px" : "32px" }}
                >
                  <MembershipPriceInfo
                    firstMonthAdjustment={
                      quote.quoteItems[0].unitPrice -
                      quote.paymentIntent.proratedAmount
                    }
                    monthlyPrice={quote.quoteItems[0].unitPrice}
                    currency={quote.currency}
                    promotion={quote.promotionLine}
                    taxLine={quote.taxLine}
                    paymentCycles={selectedMembership.firstPaymentCycles}
                    feeLines={allFeeLines}
                  />
                </div>
              </ListGroup.Item>
            )}

            <ListGroup.Item className="p-0">
              <div
                style={{
                  padding: mq.device === "mobile" ? "16px" : "24px 32px",
                }}
              >
                <MembershipCheckoutPriceV2
                  todayPrice={quote.totalPrice}
                  recurringPrice={quote.quoteItems[0].unitPriceWithTaxAndFee}
                  recurringPriceWithPromoIteration={
                    quote.quoteItems[0].unitPriceAfterDiscountWithTaxAndFee
                  }
                  currency={quote.currency}
                  selectedMembership={selectedMembership}
                  startDate={subscriptionStartDate}
                  promoLineItem={quote.promotionLine}
                />
              </div>
            </ListGroup.Item>
          </>
        )}
      </ListGroup>
    </Card>
  );
}
